// src/pages/products/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../../core/api';
import './Products.css';
import ProductsSkeleton from './ProductsSkeleton';
import { Link } from 'react-router-dom';
import { formatNumber, parseNum } from '../../utils/factorHelpers';

const EMPTY_PRODUCT = {
  code: '',
  name: '',
  unit: '',
  price: '',
  description: ''
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const nameInputRef = useRef(null);

  /* ============== Fetch ============== */
  const fetchProducts = async () => {
    try {
      const res = await api.get('/products');
      const list = res.data?.products || res.data || [];
      setProducts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('خطا در دریافت محصولات:', err);
      showMessage('خطا در دریافت محصولات', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  /* ============== Toast auto-hide ============== */
  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== Handlers ============== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        unit: form.unit.trim(),
        price: Number(form.price) || 0,
      };

      const res = await api.post('/products', payload);
      const created = res.data?.product || res.data;

      if (created && created.id) {
        setProducts((prev) => [created, ...prev]);
      } else {
        await fetchProducts();
      }

      setForm(EMPTY_PRODUCT);
      showMessage('محصول با موفقیت اضافه شد', 'success');

      // فوکوس روی اولین فیلد
      if (nameInputRef.current) nameInputRef.current.focus();
    } catch (err) {
      console.error('خطا در افزودن محصول:', err);
      showMessage(
        err.response?.data?.error || 'خطا در افزودن محصول',
        'danger'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف شود؟')) return;

    setDeletingId(id);
    try {
      await api.post(`/products/${id}/delete`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      showMessage('محصول حذف شد', 'success');
    } catch (err) {
      console.error('خطا در حذف محصول:', err);
      showMessage(
        err.response?.data?.error || 'خطا در حذف محصول',
        'danger'
      );
    } finally {
      setDeletingId(null);
    }
  };

  /* ============== Render ============== */
  if (loading) {
    return <ProductsSkeleton />;
  }

  return (
    <>
      {message && (
        <div
          className={
            messageType === 'danger' ? 'toast-danger' : 'toast-success'
          }
        >
          {message}
        </div>
      )}

      {/* ============ فرم افزودن محصول ============ */}
      <div className="card">
        <h2>افزودن محصول</h2>
        <form onSubmit={handleSubmit} id="addProductForm">
          <div className="grid">
            <div>
              <label>کد کالا</label>
              <input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="اختیاری"
              />
            </div>
            <div>
              <label>شرح کالا یا خدمات *</label>
              <input
                ref={nameInputRef}
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label>واحد اندازه‌گیری</label>
              <input
                name="unit"
                value={form.unit}
                onChange={handleChange}
                placeholder="عدد / کیلوگرم / متر ..."
              />
            </div>
            <div>
              <label>مبلغ واحد (ریال)</label>
              <input
                name="price"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={form.price ? formatNumber(parseNum(form.price)) : ''}
                onChange={(e) => {
                  const raw = parseNum(e.target.value);
                  setForm((prev) => ({ ...prev, price: raw ? String(raw) : '' }));
                }}
                placeholder="مبلغ واحد را وارد کنید"
              />
            </div>

            <div className="field-full">
              <label>توضیحات (اختیاری)</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="توضیحات دربارهٔ محصول..."
              />
            </div>
          </div>
          <div className="flexend">
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-plus" />}
              <span className="btn-label">
                {saving ? 'در حال افزودن...' : 'افزودن محصول'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    style={{ animation: 'spin 0.8s linear infinite' }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="90 150"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ============ لیست محصولات ============ */}
      <div className="card">
        <h2>لیست محصولات</h2>
        <div className="overflow">
          <table>
            <thead>
              <tr>
                <th>کد</th>
                <th>شرح کالا</th>
                <th>واحد</th>
                <th>مبلغ واحد</th>
                <th>توضیحات</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">
                    هنوز محصولی ثبت نشده است.
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id}>
                    <td>{p.code || '-'}</td>
                    <td>{p.name}</td>
                    <td>{p.unit || '-'}</td>
                    <td>
                      {Number(p.price || 0).toLocaleString('fa-IR')}
                    </td>
                    <td title={p.description || ''}>
                      {p.description
                        ? p.description.length > 40
                          ? p.description.slice(0, 40) + '…'
                          : p.description
                        : '-'}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Link
                          className="btn secondary small"
                          to={`/dashboard/products/${p.id}/edit`}
                        >
                          <i className="fa-solid fa-pen" />
                          <span className="btn-label">ویرایش</span>
                        </Link>
                        <button
                          type="button"
                          className="btn danger small"
                          onClick={() => handleDelete(p.id)}
                          disabled={deletingId === p.id}
                        >
                          {deletingId !== p.id && (
                            <i className="fa-solid fa-trash" />
                          )}
                          <span className="btn-label">
                            {deletingId === p.id ? 'در حال حذف...' : 'حذف'}
                          </span>
                          {deletingId === p.id && (
                            <span className="btn-spinner">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 50 50"
                                style={{ animation: 'spin 0.8s linear infinite' }}
                              >
                                <circle
                                  cx="25"
                                  cy="25"
                                  r="20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="5"
                                  strokeLinecap="round"
                                  strokeDasharray="90 150"
                                />
                              </svg>
                            </span>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Products;
